import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Col, Input, Row, Table } from 'antd';
import { API } from '@/services/api.service';

const matches = (needle, ...values) =>
  !needle ||
  values.some((v) =>
    String(v || '')
      .toLowerCase()
      .includes(needle)
  );

/**
 * Chart of accounts and partner register as received from AB.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @returns {JSX.Element} Two lookup tables
 */
export default function AbMasterDataTab({ companyId }) {
  const { t } = useTranslation('clientReports');
  const [search, setSearch] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['client-reports-ab-master', companyId],
    queryFn: () => API.clientReports.getAbMasterData(companyId),
  });

  const needle = search.trim().toLowerCase();
  const accounts = (data?.data?.accounts || []).filter((a) =>
    matches(needle, a.account, a.name, a.nameEn)
  );
  const partners = (data?.data?.partners || []).filter((p) =>
    matches(needle, p.code, p.name)
  );

  return (
    <div>
      <Input
        allowClear
        style={{ width: 300, marginBottom: 12 }}
        placeholder={t('ab.masterSearch')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Row gutter={[24, 16]}>
        <Col xs={24} xl={14} style={{ minWidth: 0 }}>
          <Card
            size="small"
            title={t('ab.chartOfAccounts', { count: accounts.length })}
          >
            <Table
              scroll={{ x: 'max-content' }}
              rowKey="account"
              size="small"
              loading={isLoading}
              dataSource={accounts}
              pagination={{ pageSize: 50 }}
              columns={[
                {
                  title: t('ab.account'),
                  dataIndex: 'account',
                  key: 'account',
                  width: 100,
                },
                { title: t('ab.name'), dataIndex: 'name', key: 'name' },
                { title: t('ab.nameEn'), dataIndex: 'nameEn', key: 'nameEn' },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={10} style={{ minWidth: 0 }}>
          <Card
            size="small"
            title={t('ab.partnerRegister', { count: partners.length })}
          >
            <Table
              scroll={{ x: 'max-content' }}
              rowKey="code"
              size="small"
              loading={isLoading}
              dataSource={partners}
              pagination={{ pageSize: 50 }}
              columns={[
                {
                  title: t('ab.code'),
                  dataIndex: 'code',
                  key: 'code',
                  width: 120,
                },
                { title: t('ab.name'), dataIndex: 'name', key: 'name' },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
